import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { delay, filter, map, mergeMap, take } from 'rxjs/operators';
import { UserLocalStorage } from '../../login/user-local-store';
import { DirtyComponent } from '../dirty-component';
import { DisplayProductDataService } from '../display-product-data.service';
import { ErrorButtonComponent } from '../error-button/error-button.component';
import { CONTROLLER_ADDITIONAL_FIELDS, CONTROLLER_ITEMS, CONTROLLER_OPTIONAL_ITEMS } from '../pump-data.model';
import { SaveButtonComponent } from '../save-button/save-button.component';
import { PackagingState } from '../store/packaging/packaging.reducer';
import { selectPackaging } from '../store/packaging/packaging.selector';
import { ControllerItem, PackagingItem } from './controller.model';


@Component({
  selector: 'app-controllers',
  templateUrl: './controllers.component.html',
  styleUrls: ['./controllers.component.scss'],
  standalone: false
})

export class ControllersComponent implements OnInit, OnDestroy, DirtyComponent {

  private subscriptions: Subscription[] = [];

  controllerForm!: UntypedFormGroup;
  readonly additionalFields = CONTROLLER_ADDITIONAL_FIELDS;
  controller!: Observable<ControllerItem>;

  userLocalStorage = new UserLocalStorage();

  showControllerForm = false;
  isDirty = false;

  packagingList: PackagingItem[] = [];

  originalControllerFormValue: any;

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private displayProductData: DisplayProductDataService
  ) { }



  ngOnInit() {

    const controller$ = this.route.paramMap.pipe(
      map(params => params.get('uuid')),
      mergeMap(uuid => this.displayProductData.getControllerItem(uuid!))
    );

    const packaging$ = this.store.pipe(
      select(selectPackaging),
      map((data: PackagingState) => data.data),
      filter(data => !!data && data.length > 0)
    );

    const combinedSub = combineLatest([controller$, packaging$])
      .subscribe(([controllerData, packagingList]) => {

        this.packagingList = [...packagingList!].sort(
          (a, b) => Number(a.subType.L) - Number(b.subType.L)
        );

        const fieldDefaults: Record<string, any> = {
          d: 0, d1: 0, d2: 0, e: 0, e1: 0,
          h: 0, h1: 0, h2: 0, h3: 0,
          w: 0, w1: 0, w2: 0, w3: 0,
          efficiency: 0, i_factor: 0, i_offset: 0, u_factor: 0,
          max_temp: 0, min_temp: 0,
          max_i: 0, max_freq: 0, max_p: 0, max_voc: 0, min_cable_size: 0,
          op_voc: 0, output_rated_voc: 0,
          net_weight: 0, gross_weight: 0, economical_value_for_sort_order: 0,
          max_isc: 0, max_output_voc: '', max_output_i: 0, max_output_p: 0, min_vmp: 0,
          name: '', item: '', uuid: '',
          picture: '', picture_url: '', drawing: '', drawing_url: '',
          ingress_protection_code: '', description: '', freq_range: '0-0',
        };
        const controllerFormValue: any = {};
        CONTROLLER_ITEMS.forEach(name => {
          let value = (controllerData as any)[name];
          if (name in fieldDefaults && (value === null || value === undefined)) {
            value = fieldDefaults[name];
          }

          controllerFormValue[name] = [value, CONTROLLER_OPTIONAL_ITEMS.includes(name) ? [] : Validators.required];
        });

        this.controllerForm = this.fb.group(controllerFormValue);
        this.originalControllerFormValue = this.controllerForm.value;

        const item = this.packagingList.find(
          item => item.uuid === this.controllerForm.value.packaging.uuid
        );

        if (item) {
          this.originalControllerFormValue.packaging = item.name;
        }

        this.setupFormListeners();
        this.showControllerForm = true;
      });

    this.subscriptions.push(combinedSub);

  }

  onSave() {
    const dialogRef = this.dialog.open(SaveButtonComponent, {
      width: '600px',
      disableClose: true,
      minHeight: '20vh',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updateController();
      }
    });
  }

  private setupFormListeners() {
    this.controllerForm.valueChanges.subscribe(value => {
      // your existing logic unchanged
      this.isDirty =
        JSON.stringify(this.originalControllerFormValue) !== JSON.stringify(value);
    });
  }

  updateController() {
    const user = this.userLocalStorage.loadUser();
    const data = this.controllerForm.value;
    data.author = user!.user.username;

    if (this.controllerForm.value.packaging.subType) {
      const packagingValue: any = {};
      packagingValue.name = data.packaging.uuid;
      packagingValue.w = data.packaging.subType.W;
      packagingValue.h = data.packaging.subType.H;
      packagingValue.l = data.packaging.subType.L;
      packagingValue.pack_weight = data.packaging.weight;
      data.packaging = packagingValue;
    } else {
      const result = this.packagingList.filter(item => item.name!.includes(data.packaging));
      const packaging: any = {};
      packaging.name = result[0].uuid;
      packaging.w = `${result[0].subType.W}`;
      packaging.h = `${result[0].subType.H}`;
      packaging.l = `${result[0].subType.L}`;
      packaging.pack_weight = `${result[0].weight}`;
      data.packaging = packaging;
    }

    this.displayProductData.updateController(data).subscribe(
      item => {
        this.snackBar.open(item, '', { duration: 1500, panelClass: 'hintMsg' })
          .afterDismissed()
          .pipe(delay(0))
          .subscribe(() => {
            this.ngOnInit();
            this.showControllerForm = false;
            this.isDirty = false;
          });
      },
      error => {
        this.dialog.open(ErrorButtonComponent, {
          width: '600px',
          data: JSON.parse(error.error),
          minHeight: '20vh',
        });
      },
      () => { }
    );
  }

  onDrawingSelect(event: any) {
    console.log('Drawing_url‚ selection changed:', event);
    const drawingValue = event.value;
    const drawingName = this.extractNameFromUrl(drawingValue);
    this.controllerForm.get('drawing_url')!.setValue(drawingValue);
    this.controllerForm.get('drawing')!.setValue(drawingName);
    this.controllerForm.patchValue({
      drawing: drawingName,
      drawing_url: drawingValue
    }, { emitEvent: false });
  }


  onPictureSelect($event: any) {
    console.log('Picture_url selection changed:', $event);
    const pictureValue = $event.value;
    const pictureName = this.extractNameFromUrl(pictureValue);
    this.controllerForm.get('picture_url')!.setValue(pictureValue);
    this.controllerForm.get('picture')!.setValue(pictureName);
    this.controllerForm.patchValue({
      picture: pictureName,
      picture_url: pictureValue
    }, { emitEvent: false });
  }

  private extractNameFromUrl(value: string): string {
    if (!value) return '';
    // If it's a URL, extract the name
    if (value.includes('http') || value.includes('s3')) {
      const nameMatch = value.match(/\/([^\/]+)\.[^\/]+$/);
      return nameMatch ? nameMatch[1] : value;
    }
    // Otherwise it's already a name
    return value;
  }

  canDeactivate() {
    return this.isDirty;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
